import { useState, useEffect } from 'react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Hook: verifica se un artigiano può ricevere ordini (ha completato l'onboarding Stripe).
 * Ritorna { canSell, loading }. Cache in memoria per non ripetere fetch inutili.
 */
const canSellCache = new Map(); // crafter_id -> { canSell, ts }
const CACHE_TTL_MS = 60_000;

export function useCrafterCanSell(crafterId) {
  const [state, setState] = useState({ canSell: null, loading: true });

  useEffect(() => {
    if (!crafterId) {
      setState({ canSell: false, loading: false });
      return;
    }
    const cached = canSellCache.get(crafterId);
    if (cached && Date.now() - cached.ts < CACHE_TTL_MS) {
      setState({ canSell: cached.canSell, loading: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/crafters/${crafterId}/can-sell`);
        const data = await res.json();
        const canSell = Boolean(data?.can_sell);
        canSellCache.set(crafterId, { canSell, ts: Date.now() });
        if (!cancelled) setState({ canSell, loading: false });
      } catch (_) {
        if (!cancelled) setState({ canSell: false, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [crafterId]);

  return state;
}

/**
 * Hook: batch check per una lista di crafter_id. Ritorna { results: {id: bool}, loading }.
 */
export function useCraftersCanSellBatch(crafterIds) {
  const [state, setState] = useState({ results: {}, loading: true });

  const key = (crafterIds || []).slice().sort().join(',');

  useEffect(() => {
    const ids = (crafterIds || []).filter(Boolean);
    if (ids.length === 0) {
      setState({ results: {}, loading: false });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/crafters/can-sell-batch`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ crafter_ids: ids }),
        });
        const data = await res.json();
        const results = data?.results || {};
        // Aggiorna cache singola
        Object.entries(results).forEach(([cid, val]) => {
          canSellCache.set(cid, { canSell: Boolean(val), ts: Date.now() });
        });
        if (!cancelled) setState({ results, loading: false });
      } catch (_) {
        if (!cancelled) setState({ results: {}, loading: false });
      }
    })();
    return () => { cancelled = true; };
  }, [key]);

  return state;
}
